package com.dotdrives.web.pages.givers

import com.dotdrives.controller.NewGiverController
import com.dotdrives.controller.NewGiverUiState
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.design.DotTone
import com.dotdrives.model.OrgMember
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import com.dotdrives.web.ui.DotPill
import react.ChildrenBuilder
import react.FC
import react.Key
import react.Props
import react.dom.aria.AriaPressed
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text

external interface NewGiverDialogProps : Props {
    var controller: NewGiverController
    var state: NewGiverUiState
    var currentUserId: String
    var currentUserName: String
    var members: List<OrgMember>
    var onCancel: () -> Unit
}

val NewGiverDialog = FC<NewGiverDialogProps> { props ->
    com.dotdrives.web.ui.useDialogFocus(true, "new-giver-dialog", props.onCancel)
    val (tagDraft, setTagDraft) = useState("")
    val memberOptions = if (props.members.any { it.userId == props.currentUserId }) {
        props.members
    } else {
        listOf(OrgMember(props.currentUserId, props.currentUserName, "member")) + props.members
    }

    fun addTag() {
        val tag = tagDraft.trim()
        if (tag.isNotEmpty()) {
            props.controller.setTags(props.state.tags + tag)
            setTagDraft("")
        }
    }

    div {
        className = ClassName("givers-backdrop givers-dialog-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onClick = { props.onCancel() }

        div {
            className = ClassName("givers-dialog")
            id = web.dom.ElementId("new-giver-dialog")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = if (props.state.editingGiverId == null) "New giver" else "Edit giver"
            onClick = { it.stopPropagation() }

            div {
                className = ClassName("givers-dialog-head")
                div {
                    className = ClassName("givers-dialog-title")
                    +if (props.state.editingGiverId == null) "New giver" else "Edit giver"
                }
                div {
                    className = ClassName("givers-type-toggle")
                    listOf(
                        "individual" to "👤 Individual",
                        "organization" to "🏢 Organization",
                    ).forEach { (value, label) ->
                        button {
                            type = ButtonType.buttonType
                            className = ClassName(
                                "givers-type-option" +
                                    if (props.state.giverType == value) " givers-type-option-active" else "",
                            )
                            ariaPressed = (props.state.giverType == value).unsafeCast<AriaPressed>()
                            onClick = { props.controller.setGiverType(value) }
                            +label
                        }
                    }
                }
            }

            props.state.error?.let { error ->
                DotBanner {
                    message = error
                    className = "givers-inline-banner"
                }
            }

            if (props.state.giverType == "individual") {
                div {
                    className = ClassName("givers-form-grid")
                    GiverField("First name *", props.state.firstName, "Linda", props.controller::setFirstName)
                    GiverField("Last name *", props.state.lastName, "Berry", props.controller::setLastName)
                }
                div {
                    className = ClassName("givers-form-grid")
                    GiverField(
                        "Spouse / partner",
                        props.state.spouseName,
                        "Tom Berry",
                        props.controller::setSpouseName,
                    )
                    GiverField("Phone", props.state.phone, "(555) 000-0000", props.controller::setPhone)
                }
            } else {
                div {
                    className = ClassName("givers-form-field givers-form-field-full")
                    GiverField(
                        "Organization name *",
                        props.state.displayName,
                        "Ortiz Foundation",
                        props.controller::setDisplayName,
                    )
                }
                div {
                    className = ClassName("givers-form-field givers-form-field-full")
                    GiverField("Phone", props.state.phone, "(555) 000-0000", props.controller::setPhone)
                }
            }

            div {
                className = ClassName("givers-form-field givers-form-field-full")
                GiverField("Email", props.state.email, "giver@example.org", props.controller::setEmail)
            }
            div {
                className = ClassName("givers-form-field givers-form-field-full")
                GiverField(
                    "Address",
                    props.state.address,
                    "Street, city, state, ZIP, country",
                    props.controller::setAddress,
                )
            }
            div {
                className = ClassName("givers-form-grid")
                div {
                    className = ClassName("givers-form-field")
                    div {
                        className = ClassName("givers-form-label")
                        +"Steward"
                    }
                    select {
                        ariaLabel = "Steward"
                        value = props.state.stewardUserId ?: props.currentUserId
                        onChange = { props.controller.setStewardUserId(it.currentTarget.value) }
                        memberOptions.forEach { member ->
                            option {
                                key = member.userId.unsafeCast<Key>()
                                value = member.userId
                                +member.displayName
                            }
                        }
                    }
                }
                div {
                    className = ClassName("givers-form-field")
                    div {
                        className = ClassName("givers-form-label")
                        +"Tags"
                    }
                    div {
                        className = ClassName("givers-tags-input")
                        input {
                            type = InputType.text
                            value = tagDraft
                            placeholder = "+ tag"
                            ariaLabel = "New tag"
                            onChange = { setTagDraft(it.currentTarget.value) }
                        }
                        button {
                            type = ButtonType.buttonType
                            className = ClassName("givers-add-tag")
                            disabled = tagDraft.isBlank()
                            onClick = { addTag() }
                            ariaLabel = "Add tag"
                            +"+"
                        }
                    }
                    if (props.state.tags.isNotEmpty()) {
                        div {
                            className = ClassName("givers-dialog-tags")
                            props.state.tags.forEach { tag ->
                                button {
                                    key = tag.unsafeCast<Key>()
                                    type = ButtonType.buttonType
                                    className = ClassName("givers-remove-tag")
                                    title = "Remove $tag"
                                    onClick = {
                                        props.controller.setTags(props.state.tags.filterNot { it == tag })
                                    }
                                    DotPill {
                                        text = "$tag ✕"
                                        tone = DotTone.Automation
                                        className = "givers-tag"
                                    }
                                }
                            }
                        }
                    }
                }
            }

            div {
                className = ClassName("givers-dialog-actions")
                DotButton {
                    text = "Cancel"
                    variant = DotButtonVariant.Secondary
                    onClick = props.onCancel
                    enabled = !props.state.saving
                }
                if (props.state.editingGiverId == null) DotButton {
                    text = "Save + new drive"
                    variant = DotButtonVariant.Secondary
                    enabled = !props.state.saving
                    onClick = props.controller::saveAndNewDrive
                }
                DotButton {
                    text = if (props.state.editingGiverId == null) "Save giver" else "Save changes"
                    onClick = props.controller::save
                    loading = props.state.saving
                }
            }
        }
    }
}

private fun ChildrenBuilder.GiverField(
    label: String,
    value: String,
    placeholder: String,
    onChange: (String) -> Unit,
) {
    div {
        className = ClassName("givers-form-field")
        div {
            className = ClassName("givers-form-label")
            +label
        }
        input {
            type = InputType.text
            this.value = value
            this.placeholder = placeholder
            ariaLabel = label.removeSuffix(" *")
            this.onChange = { onChange(it.currentTarget.value) }
        }
    }
}
